/* Reports - RPT-01..10.
 *
 * The point this screen has to make is that the reports are ONE decision in
 * different units, so each row names the coefficient it applies and who
 * reads it.
 *
 * Two columns, not one, because they answer different questions:
 *
 *   On screen   RPT-05 - the engine can compute it, so it can be shown with
 *               filters, today
 *   Excel file  RPT-03 - a VERIFIED layout exists, so the file can be handed
 *               to the department that reads it
 *
 * Only the MF50 is downloadable: it is the one layout checked against a real
 * client file. Inventing a layout for the other eight would risk somebody
 * forwarding it to finance as though it were the report they already know.
 *
 * The opened report takes over the page rather than opening in a dialog. A
 * 66-column table does not belong in a centred overlay: a flex child defaults
 * to `min-width: auto`, so the intrinsic width of 66 columns pushes the box
 * wider than the viewport and the left edge becomes unreachable. Full width
 * with its own horizontal scroll is both simpler and what the data wants.
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'

interface ReportRow {
  id: string
  name: string
  coefficient: string
  goes_to: string
  viewable: boolean
  downloadable: boolean
  provisional?: boolean
  provisional_reason?: string
  blocked_on?: string
  download_blocked_on?: string
}

interface TableRow {
  value_stream: string
  /* TLO & OT only: which of its four measures this line is. */
  measure?: string
  project: string
  weeks: string[]
  values: number[]
}

export function Reports({ scenario, canDownload }: { scenario: string; canDownload: boolean }) {
  const [data, setData] = useState<{ reports: ReportRow[]; note: string } | null>(null)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<{ report: ReportRow; rows: TableRow[] } | null>(null)
  const [loadingTable, setLoadingTable] = useState<string | null>(null)

  useEffect(() => {
    void api
      .reports(scenario)
      .then((d) => setData(d as unknown as { reports: ReportRow[]; note: string }))
  }, [scenario])

  // Per report, not per screen: `downloading` holds the id being produced so
  // one row's spinner does not disable the other eight.
  const download = async (reportId: string) => {
    setDownloading(reportId)
    setError(null)
    try {
      await api.downloadReport(reportId, scenario)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setDownloading(null)
    }
  }

  const view = async (report: ReportRow) => {
    setLoadingTable(report.id)
    setError(null)
    try {
      const result = await api.reportTable(report.id, scenario)
      setOpen({ report, rows: result.rows as TableRow[] })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoadingTable(null)
    }
  }

  if (!data) return <div className="card" style={{ padding: 20 }}>Loading…</div>

  // The opened report replaces the catalogue: one wide table at a time.
  if (open) {
    return (
      <ReportTable
        report={open.report}
        rows={open.rows}
        onClose={() => setOpen(null)}
        onDownload={
          open.report.downloadable && canDownload ? () => void download(open.report.id) : undefined
        }
        downloading={downloading}
      />
    )
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card page-head">
        <div className="page-title">
          <h1>Reports</h1>
          <div className="page-sub">{data.note}</div>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: '10px 14px', borderLeft: '4px solid var(--status-critical)' }}>
          <strong>⚠</strong> {error}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th className="left">Report</th>
              <th className="left">What it multiplies the decision by</th>
              <th className="left">Read by</th>
              <th className="left">On screen</th>
              <th className="left">Excel file</th>
            </tr>
          </thead>
          <tbody>
            {data.reports.map((r) => (
              <tr key={r.id}>
                <td className="left" style={{ fontWeight: 600 }}>{r.name}</td>
                <td className="left muted" style={{ fontSize: 12 }}>{r.coefficient}</td>
                <td className="left" style={{ fontSize: 12 }}>{r.goes_to}</td>

                {/* RPT-05 — viewing */}
                <td className="left">
                  {r.viewable ? (
                    <button
                      className="small"
                      onClick={() => void view(r)}
                      disabled={loadingTable === r.id}
                    >
                      {loadingTable === r.id ? 'Loading…' : 'View'}
                    </button>
                  ) : (
                    <span className="chip neutral" title={r.blocked_on}>blocked</span>
                  )}
                </td>

                {/* RPT-03 — the file itself */}
                <td className="left">
                  {r.downloadable ? (
                    canDownload ? (
                      <button
                        className="primary small"
                        onClick={() => void download(r.id)}
                        disabled={downloading !== null}
                        title={r.provisional_reason}
                      >
                        {downloading === r.id ? 'Generating…' : 'Download .xlsx'}
                      </button>
                    ) : (
                      <span className="chip neutral">not your role</span>
                    )
                  ) : (
                    <span
                      className="chip neutral"
                      title={r.download_blocked_on ?? r.blocked_on}
                      style={{ fontSize: 11 }}
                    >
                      blocked
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* RPT-05: the report on screen, full width, with its filters. */
function ReportTable({
  report,
  rows,
  onClose,
  onDownload,
  downloading,
}: {
  report: ReportRow
  rows: TableRow[]
  onClose: () => void
  onDownload?: () => void
  downloading: string | null
}) {
  const [stream, setStream] = useState('')
  const [weekly, setWeekly] = useState(false)

  const shown = stream ? rows.filter((r) => r.value_stream === stream) : rows
  const weeks = rows[0]?.weeks ?? []
  const families = [...new Set(rows.map((r) => r.value_stream))]
  const measured = rows.some((r) => r.measure)

  // The first 14 columns are days. On a report read by week, showing them is
  // noise; the filter lets the reader drop them.
  const from = weekly ? 14 : 0

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card page-head">
        <div className="page-title">
          <h1>{report.name}</h1>
          <div className="page-sub">
            {report.coefficient} · read by {report.goes_to}
          </div>
        </div>
        <div className="page-actions">
          <label className="small muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={weekly} onChange={(e) => setWeekly(e.target.checked)} />
            weekly only
          </label>
          <select value={stream} onChange={(e) => setStream(e.target.value)} className="small">
            <option value="">All value streams ({families.length})</option>
            {families.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          {onDownload && (
            <button className="primary small" onClick={onDownload} disabled={downloading !== null}>
              {downloading ? 'Generating…' : 'Download .xlsx'}
            </button>
          )}
          <button onClick={onClose}>← All reports</button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto', minWidth: 0 }}>
        {/* width:auto, not 100%: 66 columns have an intrinsic width larger
            than the card, and the table must be allowed to exceed it so the
            card scrolls rather than stretches. */}
        <table className="data-table mono" style={{ width: 'auto', minWidth: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th
                className="left"
                style={{ position: 'sticky', left: 0, zIndex: 3, background: 'var(--surface-3)' }}
              >
                Value stream
              </th>
              {measured && <th className="left">Measure</th>}
              {weeks.slice(from).map((w, i) => (
                <th key={`${w}-${i}`}>{w}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.value_stream}-${r.measure ?? ''}`}>
                <td
                  className="left"
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    background: 'var(--surface-1)',
                    fontWeight: 600,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {r.value_stream}
                </td>
                {measured && (
                  <td className="left" style={{ whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    {r.measure}
                  </td>
                )}
                {r.values.slice(from).map((v, i) => (
                  <td key={i} className="num">
                    {v === 0 ? <span className="muted">0</span> : Math.round(v).toLocaleString()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="small muted" style={{ margin: 0 }}>
        {shown.length} line{shown.length > 1 ? 's' : ''} × {weeks.length - from} buckets.
        {!weekly && ' The first 14 columns are daily, the rest weekly.'} Every figure comes from the
        calculation engine — nothing on this screen is computed in the browser.
      </p>
    </div>
  )
}
